import api from './client';

export type AgileStatus = 'todo' | 'in_progress' | 'test' | 'done' | 'closed' | 'canceled';

export interface AgileModule {
  id: number;
  code: string;
  name: string;
}

export interface AgileTaskType {
  id: number;
  code: string;
  name: string;
}

export interface AgileUser {
  id: number;
  fullname: string | null;
  username: string;
  email: string;
  is_active: number;
}

export interface AgileTask {
  id: number;
  uuid_id: string;
  title: string;
  description: string | null;
  priority: number;
  status: AgileStatus;
  due_date: string | null;
  created_on: string;
  updated_on: string;
  module_id: number;
  module_name: string;
  task_type_id: number | null;
  task_type_code: string | null;
  task_type_name: string | null;
  created_by_id: number | null;
  created_by_name: string | null;
  assigned_user_id: number | null;
  assigned_user_name: string | null;
  comment_count: number;
  attachment_count: number;
}

export interface AgileComment {
  id: number;
  comment_text: string;
  created_on: string;
  user_id: number | null;
  user_name: string | null;
}

export interface AgileHistoryItem {
  id: number;
  event_type: 'status' | 'assignee' | 'attachment_add' | 'attachment_delete' | 'relation_add' | 'relation_delete';
  from_status: AgileStatus | null;
  to_status: AgileStatus | null;
  from_assigned_user_id: number | null;
  from_assigned_user_name: string | null;
  to_assigned_user_id: number | null;
  to_assigned_user_name: string | null;
  created_on: string;
  moved_by_id: number | null;
  moved_by_name: string | null;
  details: string | null;
}

export interface AgileAttachment {
  id: number;
  file_name: string;
  file_url: string;
  mime_type: string | null;
  size_bytes: number;
  created_on: string;
  created_by_id: number | null;
  created_by_name: string | null;
}

export interface AgileRelatedTask {
  id: number;
  uuid_id: string;
  title: string;
  status: AgileStatus;
  priority: number;
  module_name: string;
  task_type_name: string | null;
  assigned_user_name: string | null;
  created_on: string;
}

export interface AgileTaskDetail extends AgileTask {
  comments: AgileComment[];
  history: AgileHistoryItem[];
  attachments: AgileAttachment[];
  related_tasks: AgileRelatedTask[];
}

export interface AgileLookups {
  modules: AgileModule[];
  taskTypes: AgileTaskType[];
  users: AgileUser[];
}

export interface AgileTaskPayload {
  title: string;
  description: string;
  moduleId: number;
  taskTypeId?: number | null;
  priority: number;
  assignedUserId?: number | null;
  dueDate?: string | null;
  relatedSourceUuidId?: string;
}

export interface AgileTaskUpdatePayload {
  title?: string;
  description?: string;
  moduleId?: number;
  taskTypeId?: number | null;
  priority?: number;
  assignedUserId?: number | null;
  dueDate?: string | null;
  status?: AgileStatus;
  assignToMe?: boolean;
}

export async function fetchAgileLookups(): Promise<AgileLookups> {
  const res = await api.get<AgileLookups>('/agile/lookups');
  return res.data;
}

export async function fetchAgileBoard(): Promise<AgileTask[]> {
  const res = await api.get<AgileTask[]>('/agile/board');
  return res.data;
}

export async function fetchAgileTaskDetail(uuid_id: string): Promise<AgileTaskDetail> {
  const res = await api.get<AgileTaskDetail>(`/agile/tasks/${uuid_id}`);
  return res.data;
}

export async function createAgileTask(payload: AgileTaskPayload): Promise<{ ok: boolean; uuid_id: string }> {
  const res = await api.post<{ ok: boolean; uuid_id: string }>('/agile/tasks', payload);
  return res.data;
}

export async function updateAgileTask(uuid_id: string, payload: AgileTaskUpdatePayload): Promise<void> {
  await api.patch(`/agile/tasks/${uuid_id}`, payload);
}

export async function deleteAgileTask(uuid_id: string): Promise<void> {
  await api.delete(`/agile/tasks/${uuid_id}`);
}

export async function addAgileComment(uuid_id: string, comment: string, mentionedUserIds?: number[]): Promise<AgileComment> {
  const res = await api.post<AgileComment>(`/agile/tasks/${uuid_id}/comments`, {
    comment,
    mentionedUserIds: mentionedUserIds && mentionedUserIds.length > 0 ? mentionedUserIds : undefined,
  });
  return res.data;
}

export async function addAgileAttachment(uuid_id: string, file: File): Promise<AgileAttachment> {
  const formData = new FormData();
  formData.append('file', file);
  const res = await api.post<AgileAttachment>(`/agile/tasks/${uuid_id}/attachments`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data;
}

export async function deleteAgileAttachment(uuid_id: string, attachmentId: number): Promise<void> {
  await api.delete(`/agile/tasks/${uuid_id}/attachments/${attachmentId}`);
}

export async function searchAgileTasks(queryText: string, excludeUuid?: string): Promise<AgileRelatedTask[]> {
  const res = await api.get<AgileRelatedTask[]>('/agile/tasks/search', {
    params: {
      q: queryText,
      excludeUuid,
    },
  });
  return res.data;
}

export async function addAgileRelation(uuid_id: string, relatedUuidId: string): Promise<void> {
  await api.post(`/agile/tasks/${uuid_id}/relations`, { relatedUuidId });
}

export async function deleteAgileRelation(uuid_id: string, relatedUuidId: string): Promise<void> {
  await api.delete(`/agile/tasks/${uuid_id}/relations/${relatedUuidId}`);
}
